// src/pages/GuildEvents.js - 길드 이벤트 관리 페이지
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  orderBy,
  getDoc 
} from "firebase/firestore";

const EVENT_TYPES = [
  { id: "tournament", name: "토너먼트", icon: "🏆", color: "#f59e0b" },
  { id: "practice", name: "연습 세션", icon: "🏋️", color: "#10b981" },
  { id: "social", name: "친목 모임", icon: "🎉", color: "#8b5cf6" },
  { id: "challenge", name: "도전과제", icon: "🎯", color: "#ef4444" },
  { id: "meeting", name: "길드 회의", icon: "💬", color: "#3b82f6" }
];

const REWARD_TYPES = [
  { id: "mmr", name: "MMR 보너스", icon: "📈" },
  { id: "contribution", name: "기여도", icon: "🌟" },
  { id: "badge", name: "뱃지", icon: "🏅" },
  { id: "title", name: "칭호", icon: "👑" }
];

const ATTENDANCE_REWARDS = [
  { days: 3, reward: { type: "contribution", amount: 10 }, name: "3일 연속" },
  { days: 7, reward: { type: "mmr", amount: 25 }, name: "1주 연속" },
  { days: 14, reward: { type: "badge", amount: 1 }, name: "2주 연속" },
  { days: 30, reward: { type: "title", amount: 1 }, name: "1달 연속" }
];

// 이벤트 상태 계산
function getEventStatus(startDate, endDate) {
  const now = new Date();
  const start = startDate.toDate ? startDate.toDate() : new Date(startDate);
  const end = endDate.toDate ? endDate.toDate() : new Date(endDate);
  
  if (now < start) return { status: "upcoming", text: "예정", color: "#f59e0b" };
  if (now > end) return { status: "ended", text: "종료", color: "#6b7280" };
  return { status: "ongoing", text: "진행중", color: "#10b981" };
}

// 날짜 포맷팅
function formatDate(date) {
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
}

function GuildEvents() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  
  const [loading, setLoading] = useState(true);
  const [myGuild, setMyGuild] = useState(null);
  const [guildMembers, setGuildMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState("events"); // events, attendance, rewards, tournaments
  
  // 권한 체크
  const [userRole, setUserRole] = useState("");
  const isLeader = userRole === "길드장";
  const isSubLeader = userRole === "부길드장";
  const canCreateEvents = isLeader || isSubLeader;
  
  // 이벤트 생성 상태
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    type: "tournament",
    startDate: "",
    endDate: "",
    maxParticipants: 20,
    rewards: [],
    requirementMMR: 0
  });
  
  // 출석 체크 상태
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceStreak, setAttendanceStreak] = useState(0);
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  
  // 보상 관리 상태
  const [rewardHistory, setRewardHistory] = useState([]);
  const [availableRewards, setAvailableRewards] = useState([]);

  // 토너먼트 관련 상태
  const [tournaments, setTournaments] = useState([]);
  const [showCreateTournament, setShowCreateTournament] = useState(false);
  const [newTournament, setNewTournament] = useState({
    title: "",
    description: "",
    date: "",
    location: "",
    maxParticipants: 16,
    deadline: "",
    sport: ""
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    loadGuildData();
  }, [user, navigate]);

  const loadGuildData = async () => {
    try {
      // 길드 정보 가져오기
      const memberQuery = query(
        collection(db, "guildMembers"), 
        where("userId", "==", user.id)
      );
      const memberSnap = await getDocs(memberQuery);
      
      if (memberSnap.empty) {
        navigate("/guild");
        return;
      }

      const memberData = memberSnap.docs[0].data();
      const guildId = memberData.guildId;
      setUserRole(memberData.role || memberData.tier || "임시회원");

      const guildRef = doc(db, "guilds", guildId);
      const guildSnap = await getDoc(guildRef);
      
      if (guildSnap.exists()) {
        const guildData = { id: guildSnap.id, ...guildSnap.data() };
        setMyGuild(guildData);
        
        // 길드 멤버 로드
        await loadGuildMembers(guildId);
        
        // 이벤트 데이터 로드
        await loadEvents(guildId);
        await loadAttendanceData(guildId);
        await loadRewardData(guildId);
        await loadTournaments(guildId);
      }
    } catch (error) {
      console.error("길드 데이터 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadGuildMembers = async (guildId) => {
    try {
      const membersQuery = query(
        collection(db, "guildMembers"),
        where("guildId", "==", guildId)
      );
      const membersSnap = await getDocs(membersQuery);
      
      const membersData = [];
      for (const memberDoc of membersSnap.docs) {
        const memberData = memberDoc.data();
        
        // 사용자 정보 가져오기
        const userQuery = query(
          collection(db, "users"),
          where("id", "==", memberData.userId)
        );
        const userSnap = await getDocs(userQuery);
        
        if (!userSnap.empty) {
          const userData = userSnap.docs[0].data();
          membersData.push({
            id: memberData.userId,
            ...memberData,
            nickname: userData.nickname || memberData.nickname || userData.id,
            profileImg: userData.profileImg || '',
            lastLogin: userData.lastLoginDate?.toDate() || userData.lastLogin?.toDate() || new Date(),
            guildTier: memberData.role || memberData.tier || "임시회원",
            guildRole: memberData.guildRole || ""
          });
        }
      }
      
      setGuildMembers(membersData);
    } catch (error) {
      console.error("길드 멤버 로드 실패:", error);
    }
  };

  const loadEvents = async (guildId) => {
    try {
      const eventsQuery = query(
        collection(db, "guildEvents"),
        where("guildId", "==", guildId),
        orderBy("startDate", "desc")
      );
      const eventsSnap = await getDocs(eventsQuery);
      const eventsData = eventsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEvents(eventsData);
    } catch (error) {
      console.error("이벤트 로드 실패:", error);
    }
  };

  const loadAttendanceData = async (guildId) => {
    try {
      // 오늘 출석 체크 여부
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todayQuery = query(
        collection(db, "guildAttendance"),
        where("guildId", "==", guildId),
        where("userId", "==", user.id),
        where("date", ">=", today)
      );
      const todaySnap = await getDocs(todayQuery);
      setTodayAttendance(!todaySnap.empty ? todaySnap.docs[0].data() : null);
      
      // 이번 달 출석 데이터
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      const monthQuery = query(
        collection(db, "guildAttendance"),
        where("guildId", "==", guildId),
        where("userId", "==", user.id),
        where("date", ">=", monthStart),
        where("date", "<=", monthEnd),
        orderBy("date", "desc")
      );
      const monthSnap = await getDocs(monthQuery);
      const monthData = monthSnap.docs.map(doc => doc.data());
      setMonthlyAttendance(monthData);
      
      // 연속 출석 계산
      calculateAttendanceStreak(monthData);
      
    } catch (error) {
      console.error("출석 데이터 로드 실패:", error);
    }
  };

  const loadRewardData = async (guildId) => {
    try {
      const rewardQuery = query(
        collection(db, "guildRewards"),
        where("guildId", "==", guildId),
        where("userId", "==", user.id),
        orderBy("createdAt", "desc")
      );
      const rewardSnap = await getDocs(rewardQuery);
      const rewardData = rewardSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRewardHistory(rewardData);
      
      // 받을 수 있는 보상 계산
      calculateAvailableRewards();
      
    } catch (error) {
      console.error("보상 데이터 로드 실패:", error);
    }
  };

  const loadTournaments = async (guildId) => {
    try {
      const tournamentsQuery = query(
        collection(db, "guildTournaments"),
        where("guildId", "==", guildId),
        orderBy("createdAt", "desc")
      );
      const tournamentsSnap = await getDocs(tournamentsQuery);
      const tournamentsData = tournamentsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTournaments(tournamentsData);
    } catch (error) {
      console.error("토너먼트 로드 실패:", error);
    }
  };

  const calculateAttendanceStreak = (attendanceData) => {
    if (attendanceData.length === 0) {
      setAttendanceStreak(0);
      return;
    }
    
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // 연속 일수 계산 (가장 최근부터 역순으로)
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      
      const hasAttendance = attendanceData.some(attendance => {
        const attendanceDate = attendance.date.toDate();
        attendanceDate.setHours(0, 0, 0, 0);
        return attendanceDate.getTime() === checkDate.getTime();
      });
      
      if (hasAttendance) {
        streak++;
      } else if (i > 0) { // 오늘은 제외하고 연속성 체크
        break;
      }
    }
    
    setAttendanceStreak(streak);
  };

  const calculateAvailableRewards = () => {
    const available = ATTENDANCE_REWARDS.filter(reward => {
      return attendanceStreak >= reward.days && 
             !rewardHistory.some(r => r.source === `attendance_${reward.days}`);
    });
    setAvailableRewards(available);
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.startDate || !newEvent.endDate) {
      alert("필수 정보를 모두 입력해주세요.");
      return;
    }

    try {
      await addDoc(collection(db, "guildEvents"), {
        ...newEvent,
        guildId: myGuild.id,
        createdBy: user.id,
        createdAt: serverTimestamp(),
        startDate: new Date(newEvent.startDate),
        endDate: new Date(newEvent.endDate),
        participants: [],
        status: "active"
      });

      alert("이벤트가 생성되었습니다!");
      setShowCreateEvent(false);
      setNewEvent({
        title: "",
        description: "",
        type: "tournament",
        startDate: "",
        endDate: "",
        maxParticipants: 20,
        rewards: [],
        requirementMMR: 0
      });
      
      loadEvents(myGuild.id);
    } catch (error) {
      console.error("이벤트 생성 실패:", error);
      alert("이벤트 생성에 실패했습니다.");
    }
  };

  const handleCreateTournament = async () => {
    if (!newTournament.title || !newTournament.date || !newTournament.deadline) {
      alert("필수 정보를 모두 입력해주세요.");
      return;
    }

    try {
      await addDoc(collection(db, "guildTournaments"), {
        ...newTournament,
        guildId: myGuild.id,
        createdBy: user.id,
        createdAt: serverTimestamp(),
        date: new Date(newTournament.date),
        deadline: new Date(newTournament.deadline),
        sport: myGuild.sport, // 길드의 스포츠로 자동 설정
        participants: [],
        status: "recruiting", // recruiting, ongoing, completed
        contributionRewards: {
          winner: 50,
          runnerUp: 30,
          semifinalist: 20,
          participant: 10
        }
      });

      alert("토너먼트가 생성되었습니다!");
      setShowCreateTournament(false);
      setNewTournament({
        title: "",
        description: "",
        date: "",
        location: "",
        maxParticipants: 16,
        deadline: "",
        sport: ""
      });
      
      loadTournaments(myGuild.id);
    } catch (error) {
      console.error("토너먼트 생성 실패:", error);
      alert("토너먼트 생성에 실패했습니다.");
    }
  };

  const handleAttendanceCheck = async () => {
    if (todayAttendance) {
      alert("오늘은 이미 출석체크를 완료했습니다!");
      return;
    }

    try {
      const today = new Date();
      await addDoc(collection(db, "guildAttendance"), {
        guildId: myGuild.id,
        userId: user.id,
        nickname: user.nickname,
        date: today,
        createdAt: serverTimestamp()
      });

      // 출석 보상 지급
      const contributionReward = 5 + Math.floor(attendanceStreak / 7) * 2;
      await addDoc(collection(db, "guildRewards"), {
        guildId: myGuild.id,
        userId: user.id,
        type: "contribution",
        amount: contributionReward,
        source: "daily_attendance",
        description: `일일 출석 보상 (+${contributionReward} 기여도)`,
        createdAt: serverTimestamp()
      });

      alert(`출석체크 완료! +${contributionReward} 기여도를 받았습니다!`);
      loadAttendanceData(myGuild.id);
      loadRewardData(myGuild.id);
      
    } catch (error) {
      console.error("출석체크 실패:", error);
      alert("출석체크에 실패했습니다.");
    }
  };

  const handleJoinEvent = async (eventId) => {
    try {
      const eventRef = doc(db, "guildEvents", eventId);
      const eventSnap = await getDoc(eventRef);
      const eventData = eventSnap.data();
      
      if (eventData.participants?.includes(user.id)) {
        alert("이미 참가한 이벤트입니다.");
        return;
      }
      
      if (eventData.participants?.length >= eventData.maxParticipants) {
        alert("참가 인원이 가득 찼습니다.");
        return;
      }
      
      await updateDoc(eventRef, {
        participants: [...(eventData.participants || []), user.id]
      });
      
      alert("이벤트에 참가했습니다!");
      loadEvents(myGuild.id);
      
    } catch (error) {
      console.error("이벤트 참가 실패:", error);
      alert("이벤트 참가에 실패했습니다.");
    }
  };

  const handleJoinTournament = async (tournamentId) => {
    try {
      const tournamentRef = doc(db, "guildTournaments", tournamentId);
      const tournamentSnap = await getDoc(tournamentRef);
      const tournamentData = tournamentSnap.data();
      
      if (tournamentData.participants?.includes(user.id)) {
        alert("이미 참가한 토너먼트입니다.");
        return;
      }
      
      if (tournamentData.participants?.length >= tournamentData.maxParticipants) {
        alert("참가 인원이 가득 찼습니다.");
        return;
      }

      // 마감일 확인
      const deadline = tournamentData.deadline.toDate();
      if (new Date() > deadline) {
        alert("토너먼트 신청 마감일이 지났습니다.");
        return;
      }
      
      await updateDoc(tournamentRef, {
        participants: [...(tournamentData.participants || []), user.id]
      });
      
      alert("토너먼트에 참가 신청했습니다!");
      loadTournaments(myGuild.id);
      
    } catch (error) {
      console.error("토너먼트 참가 실패:", error);
      alert("토너먼트 참가에 실패했습니다.");
    }
  };

  const handleClaimReward = async (reward) => {
    try {
      await addDoc(collection(db, "guildRewards"), {
        guildId: myGuild.id,
        userId: user.id,
        type: reward.reward.type,
        amount: reward.reward.amount,
        source: `attendance_${reward.days}`,
        description: `${reward.name} 출석 보상`,
        createdAt: serverTimestamp()
      });

      alert(`${reward.name} 보상을 받았습니다!`);
      loadRewardData(myGuild.id);
      
    } catch (error) {
      console.error("보상 수령 실패:", error);
      alert("보상 수령에 실패했습니다.");
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        fontSize: 18
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 50,
            height: 50,
            border: "4px solid rgba(255,255,255,0.3)",
            borderTop: "4px solid #fff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px"
          }}></div>
          🎯 길드 이벤트 로딩 중...
        </div>
      </div>
    );
  }

  if (!myGuild) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff"
      }}>
        <div style={{
          background: "#fff",
          borderRadius: 20,
          padding: 40,
          textAlign: "center",
          color: "#374151",
          maxWidth: 400
        }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🏰</div>
          <h3 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>
            길드에 가입되어 있지 않습니다
          </h3>
          <p style={{ marginBottom: 24, color: "#6b7280" }}>
            길드 이벤트를 이용하려면 먼저 길드에 가입하세요.
          </p>
          <button
            onClick={() => {
              const myGuildId = localStorage.getItem('myGuildId');
              if (myGuildId) {
                navigate(`/guild/${myGuildId}`);
              } else {
                navigate('/guild');
              }
            }}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            길드 페이지로 가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      padding: 20
    }}>
      <div style={{
        maxWidth: 1200,
        margin: "0 auto"
      }}>
        {/* 헤더 */}
        <div style={{
          background: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(20px)",
          borderRadius: 24,
          padding: 40,
          marginBottom: 24,
          color: "#fff"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16
          }}>
            <div>
              <h1 style={{
                fontSize: 32,
                fontWeight: 700,
                margin: 0,
                marginBottom: 8
              }}>
                🎯 {myGuild.name} 이벤트
              </h1>
              <p style={{
                fontSize: 16,
                opacity: 0.8,
                margin: 0
              }}>
                길드 이벤트 관리 및 출석 체크 시스템
              </p>
            </div>
            
            <div style={{ display: "flex", gap: 16 }}>
              {canCreateEvents && (
                <>
                  <button
                    onClick={() => setShowCreateEvent(true)}
                    style={{
                      padding: "12px 20px",
                      background: "rgba(255,255,255,0.9)",
                      color: "#667eea",
                      border: "none",
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer"
                    }}
                  >
                    ➕ 이벤트 생성
                  </button>
                  
                  <button
                    onClick={() => setShowCreateTournament(true)}
                    style={{
                      padding: "12px 20px",
                      background: "rgba(255,255,255,0.9)",
                      color: "#f59e0b",
                      border: "none",
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer"
                    }}
                  >
                    🏆 토너먼트 생성
                  </button>
                </>
              )}
              
              <button 
                onClick={() => {
                  const myGuildId = localStorage.getItem('myGuildId');
                  if (myGuildId) {
                    navigate(`/guild/${myGuildId}`);
                  } else {
                    navigate('/guild');
                  }
                }}
                style={{
                  padding: "12px 24px",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                길드 페이지로 가기
              </button>
            </div>
          </div>
          
          {/* 간단한 통계 */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 16,
            opacity: 0.9
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{events.length}</div>
              <div style={{ fontSize: 14 }}>진행중 이벤트</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{tournaments.length}</div>
              <div style={{ fontSize: 14 }}>길드 토너먼트</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{attendanceStreak}</div>
              <div style={{ fontSize: 14 }}>연속 출석일</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{guildMembers.length}</div>
              <div style={{ fontSize: 14 }}>길드 멤버</div>
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div style={{
          display: "flex",
          gap: 8,
          marginBottom: 24,
          background: "rgba(255,255,255,0.1)",
          borderRadius: 16,
          padding: 8,
          overflowX: "auto"
        }}>
          {[
            { id: 'events', label: '🎯 이벤트 관리' },
            { id: 'tournaments', label: '🏆 길드 토너먼트' },
            { id: 'attendance', label: '📅 출석 체크' },
            { id: 'rewards', label: '🎁 보상 관리' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: "0 0 auto",
                padding: "12px 16px",
                background: activeTab === tab.id ? "rgba(255,255,255,0.9)" : "transparent",
                color: activeTab === tab.id ? "#374151" : "rgba(255,255,255,0.8)",
                border: "none",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
                whiteSpace: "nowrap"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 탭 콘텐츠 */}
        <div style={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: 24,
          padding: 32,
          minHeight: 500
        }}>
          {activeTab === 'events' && (
            <EventsTab 
              events={events}
              user={user}
              onJoinEvent={handleJoinEvent}
            />
          )}
          
          {activeTab === 'tournaments' && (
            <TournamentsTab 
              tournaments={tournaments}
              user={user}
              canCreateEvents={canCreateEvents}
              myGuild={myGuild}
              guildMembers={guildMembers}
              onJoinTournament={handleJoinTournament}
            />
          )}
          
          {activeTab === 'attendance' && (
            <AttendanceTab 
              todayAttendance={todayAttendance}
              attendanceStreak={attendanceStreak}
              monthlyAttendance={monthlyAttendance}
              onAttendanceCheck={handleAttendanceCheck}
            />
          )}
          
          {activeTab === 'rewards' && (
            <RewardsTab 
              rewardHistory={rewardHistory}
              availableRewards={availableRewards}
              onClaimReward={handleClaimReward}
            />
          )}
        </div>
      </div>

      {/* 이벤트 생성 모달 */}
      {showCreateEvent && (
        <CreateEventModal 
          newEvent={newEvent}
          setNewEvent={setNewEvent}
          onCreateEvent={handleCreateEvent}
          onClose={() => setShowCreateEvent(false)}
        />
      )}

      {/* 토너먼트 생성 모달 */}
      {showCreateTournament && (
        <CreateTournamentModal 
          newTournament={newTournament}
          setNewTournament={setNewTournament}
          onCreateTournament={handleCreateTournament}
          onClose={() => setShowCreateTournament(false)}
          guildSport={myGuild.sport}
        />
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

// 이벤트 관리 탭
function EventsTab({ events, user, onJoinEvent }) {
  return (
    <div>
      <h3 style={{
        fontSize: 24,
        fontWeight: 800,
        color: "#1f2937",
        marginBottom: 24,
        display: "flex",
        alignItems: "center",
        gap: 12
      }}>
        🎯 길드 이벤트
      </h3>
      
      {events.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: 60,
          color: "#9ca3af"
        }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🎯</div>
          <h4 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, color: "#374151" }}>
            아직 이벤트가 없습니다
          </h4>
          <p style={{ color: "#6b7280" }}>
            첫 번째 길드 이벤트를 생성해보세요!
          </p>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gap: 16
        }}>
          {events.map(event => {
            const eventType = EVENT_TYPES.find(t => t.id === event.type);
            const status = getEventStatus(event.startDate, event.endDate);
            const isParticipant = event.participants?.includes(user.id);
            
            return (
              <div key={event.id} style={{
                background: "#fff",
                borderRadius: 16,
                padding: 24,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                border: "1px solid #f1f5f9"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: 16
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 8
                    }}>
                      <span style={{ fontSize: 24 }}>{eventType?.icon}</span>
                      <h3 style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#1f2937",
                        margin: 0
                      }}>
                        {event.title}
                      </h3>
                      <span style={{
                        fontSize: 12,
                        padding: "4px 8px",
                        borderRadius: 12,
                        background: `${status.color}20`,
                        color: status.color,
                        fontWeight: 600
                      }}>
                        {status.text}
                      </span>
                    </div>
                    
                    <p style={{
                      fontSize: 14,
                      color: "#6b7280",
                      margin: 0,
                      marginBottom: 12,
                      lineHeight: 1.5
                    }}>
                      {event.description}
                    </p>
                    
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 12,
                      fontSize: 13,
                      color: "#6b7280"
                    }}>
                      <div>
                        <strong>시작:</strong> {formatDate(event.startDate)}
                      </div>
                      <div>
                        <strong>종료:</strong> {formatDate(event.endDate)}
                      </div>
                      <div>
                        <strong>참가자:</strong> {event.participants?.length || 0}/{event.maxParticipants}명
                      </div>
                      {event.requirementMMR > 0 && (
                        <div>
                          <strong>최소 MMR:</strong> {event.requirementMMR}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div style={{
                    display: "flex",
                    gap: 8
                  }}>
                    {status.status === "ongoing" && !isParticipant && (
                      <button
                        onClick={() => onJoinEvent(event.id)}
                        style={{
                          padding: "8px 16px",
                          background: "linear-gradient(135deg, #667eea, #764ba2)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        참가하기
                      </button>
                    )}
                    
                    {isParticipant && (
                      <span style={{
                        padding: "8px 16px",
                        background: "#d1fae5",
                        color: "#059669",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600
                      }}>
                        ✓ 참가중
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 토너먼트 탭
function TournamentsTab({ tournaments, user, canCreateEvents, myGuild, guildMembers, onJoinTournament }) {
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const getTournamentStatus = (tournament) => {
    const now = new Date();
    const deadline = tournament.deadline.toDate();
    const eventDate = tournament.date.toDate();
    
    if (now < deadline) return { status: "recruiting", text: "모집중", color: "#10b981" };
    if (now < eventDate) return { status: "waiting", text: "대기중", color: "#f59e0b" };
    if (tournament.status === "completed") return { status: "completed", text: "완료", color: "#6b7280" };
    return { status: "ongoing", text: "진행중", color: "#ef4444" };
  };

  return (
    <div>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 24
      }}>
        <h3 style={{
          fontSize: 24,
          fontWeight: 800,
          color: "#1f2937",
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: 12
        }}>
          🏆 길드 토너먼트
        </h3>
        
        {canCreateEvents && (
          <div style={{
            fontSize: 14,
            color: "#6b7280",
            background: "#f3f4f6",
            padding: "8px 12px",
            borderRadius: 8
          }}>
            💡 토너먼트 생성은 길드장/부길드장만 가능합니다
          </div>
        )}
      </div>
      
      {tournaments.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: 60,
          color: "#9ca3af"
        }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🏆</div>
          <h4 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, color: "#374151" }}>
            아직 토너먼트가 없습니다
          </h4>
          <p style={{ color: "#6b7280" }}>
            길드 토너먼트를 개최해보세요!
          </p>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gap: 16
        }}>
          {tournaments.map(tournament => {
            const status = getTournamentStatus(tournament);
            const isParticipant = tournament.participants?.includes(user.id);
            const canJoin = status.status === "recruiting" && !isParticipant;
            
            return (
              <div key={tournament.id} style={{
                background: "#fff",
                borderRadius: 16,
                padding: 24,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                border: "1px solid #f1f5f9"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: 16
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 8
                    }}>
                      <span style={{ fontSize: 24 }}>🏆</span>
                      <h3 style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#1f2937",
                        margin: 0
                      }}>
                        {tournament.title}
                      </h3>
                      <span style={{
                        fontSize: 12,
                        padding: "4px 8px",
                        borderRadius: 12,
                        background: `${status.color}20`,
                        color: status.color,
                        fontWeight: 600
                      }}>
                        {status.text}
                      </span>
                    </div>
                    
                    <p style={{
                      fontSize: 14,
                      color: "#6b7280",
                      margin: 0,
                      marginBottom: 12,
                      lineHeight: 1.5
                    }}>
                      {tournament.description}
                    </p>
                    
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 12,
                      fontSize: 13,
                      color: "#6b7280"
                    }}>
                      <div>
                        <strong>경기일:</strong> {formatDate(tournament.date)}
                      </div>
                      <div>
                        <strong>신청마감:</strong> {formatDate(tournament.deadline)}
                      </div>
                      <div>
                        <strong>장소:</strong> {tournament.location || "미정"}
                      </div>
                      <div>
                        <strong>참가자:</strong> {tournament.participants?.length || 0}/{tournament.maxParticipants}명
                      </div>
                      <div>
                        <strong>종목:</strong> {myGuild.sport}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8
                  }}>
                    {canJoin && (
                      <button
                        onClick={() => onJoinTournament(tournament.id)}
                        style={{
                          padding: "8px 16px",
                          background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        참가 신청
                      </button>
                    )}
                    
                    {isParticipant && (
                      <span style={{
                        padding: "8px 16px",
                        background: "#fef3c7",
                        color: "#d97706",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        textAlign: "center"
                      }}>
                        ✓ 참가 신청됨
                      </span>
                    )}
                    
                    {canCreateEvents && status.status !== "recruiting" && (
                      <button
                        onClick={() => setSelectedTournament(tournament)}
                        style={{
                          padding: "6px 12px",
                          background: "#f3f4f6",
                          color: "#374151",
                          border: "1px solid #d1d5db",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: "pointer"
                        }}
                      >
                        결과 관리
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 기여도 보상 정보 */}
                <div style={{
                  background: "#f8fafc",
                  padding: 12,
                  borderRadius: 8,
                  marginTop: 12
                }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: 4
                  }}>
                    🌟 기여도 보상
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: "#6b7280",
                    display: "flex",
                    gap: 16
                  }}>
                    <span>🥇 우승: +50</span>
                    <span>🥈 준우승: +30</span>
                    <span>🥉 4강: +20</span>
                    <span>👥 참가: +10</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 출석 체크 탭
function AttendanceTab({ 
  todayAttendance, 
  attendanceStreak, 
  monthlyAttendance, 
  onAttendanceCheck 
}) {
  // 이번 달 캘린더 생성
  const generateCalendar = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      const isCurrentMonth = current.getMonth() === month;
      const isToday = current.toDateString() === today.toDateString();
      const hasAttendance = monthlyAttendance.some(attendance => {
        const attendanceDate = attendance.date.toDate();
        return attendanceDate.toDateString() === current.toDateString();
      });
      
      days.push({
        date: new Date(current),
        day: current.getDate(),
        isCurrentMonth,
        isToday,
        hasAttendance
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const calendar = generateCalendar();
  const today = new Date();

  return (
    <div>
      <h3 style={{
        fontSize: 24,
        fontWeight: 800,
        color: "#1f2937",
        marginBottom: 24,
        display: "flex",
        alignItems: "center",
        gap: 12
      }}>
        📅 출석 체크
      </h3>
      
      <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 24
      }}>
        {/* 출석 캘린더 */}
        <div style={{
          background: "#fff",
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20
          }}>
            <h4 style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#1f2937",
              margin: 0
            }}>
              📅 {today.getFullYear()}년 {today.getMonth() + 1}월 출석 현황
            </h4>
            
            {!todayAttendance && (
              <button
                onClick={onAttendanceCheck}
                style={{
                  padding: "12px 20px",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)"
                }}
              >
                ✓ 출석체크
              </button>
            )}
            
            {todayAttendance && (
              <span style={{
                padding: "12px 20px",
                background: "#d1fae5",
                color: "#059669",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600
              }}>
                ✓ 출석완료
              </span>
            )}
          </div>
          
          {/* 달력 */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
            marginTop: 8
          }}>
            {["일", "월", "화", "수", "목", "금", "토"].map((d, i) => (
              <div key={i} style={{
                textAlign: "center",
                color: "#9ca3af",
                fontWeight: 600,
                fontSize: 14,
                padding: 6
              }}>{d}</div>
            ))}
            {calendar.map((day, idx) => (
              <div key={idx} style={{
                textAlign: "center",
                padding: 8,
                borderRadius: 8,
                background: day.hasAttendance
                  ? "linear-gradient(135deg, #667eea, #10b981)"
                  : day.isToday
                    ? "#fde68a"
                    : "transparent",
                color: day.hasAttendance
                  ? "#fff"
                  : day.isToday
                    ? "#b45309"
                    : day.isCurrentMonth
                      ? "#374151"
                      : "#d1d5db",
                fontWeight: day.hasAttendance ? 700 : 500,
                border: day.isToday ? "2px solid #fbbf24" : "none"
              }}>
                {day.day}
              </div>
            ))}
          </div>
        </div>

        {/* 출석 정보 */}
        <div style={{
          background: "#fff",
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
        }}>
          <h4 style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#1f2937",
            margin: 0,
            marginBottom: 16
          }}>
            🔥 연속 출석: <span style={{ color: "#10b981" }}>{attendanceStreak}</span>일
          </h4>
          
          <div style={{
            background: "#f0fdf4",
            padding: 16,
            borderRadius: 12,
            marginBottom: 16
          }}>
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#166534",
              marginBottom: 8
            }}>
              이번 달 출석률
            </div>
            <div style={{
              fontSize: 24,
              fontWeight: 700,
              color: "#16a34a"
            }}>
              {Math.round((monthlyAttendance.length / new Date().getDate()) * 100)}%
            </div>
          </div>
          
          <div style={{
            fontSize: 12,
            color: "#6b7280"
          }}>
            <div style={{ marginBottom: 8 }}>📊 출석 통계</div>
            <div>• 이번 달: {monthlyAttendance.length}일</div>
            <div>• 연속 출석: {attendanceStreak}일</div>
            <div>• 다음 보상까지: {7 - (attendanceStreak % 7)}일</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 보상 관리 탭
function RewardsTab({ rewardHistory, availableRewards, onClaimReward }) {
  return (
    <div>
      <h3 style={{
        fontSize: 24,
        fontWeight: 800,
        color: "#1f2937",
        marginBottom: 24,
        display: "flex",
        alignItems: "center",
        gap: 12
      }}>
        🎁 보상 관리
      </h3>
      
      <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 24
      }}>
        {/* 보상 내역 */}
        <div style={{
          background: "#fff",
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
        }}>
          <h4 style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#1f2937",
            margin: 0,
            marginBottom: 16
          }}>🏆 보상 내역</h4>
          
          <div style={{
            maxHeight: 400,
            overflowY: "auto",
            paddingRight: 8
          }}>
            {rewardHistory.length === 0 ? (
              <div style={{
                textAlign: "center",
                padding: 40,
                color: "#9ca3af"
              }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
                <div>아직 받은 보상이 없습니다</div>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {rewardHistory.map((reward, idx) => (
                  <div key={idx} style={{
                    padding: 16,
                    background: "#f8fafc",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4
                    }}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontWeight: 600,
                        color: "#374151"
                      }}>
                        <span>{REWARD_TYPES.find(rt => rt.id === reward.type)?.icon}</span>
                        <span>{REWARD_TYPES.find(rt => rt.id === reward.type)?.name}</span>
                        {reward.amount > 0 && (
                          <span style={{ color: "#10b981" }}>+{reward.amount}</span>
                        )}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: "#9ca3af"
                      }}>
                        {reward.createdAt?.toDate?.().toLocaleDateString?.() || '-'}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 14,
                      color: "#6b7280"
                    }}>
                      {reward.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 수령 가능한 보상 */}
        <div style={{
          background: "#fff",
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
        }}>
          <h4 style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#1f2937",
            margin: 0,
            marginBottom: 16
          }}>🎁 받을 수 있는 보상</h4>
          
          {availableRewards.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: 40,
              color: "#9ca3af"
            }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⏰</div>
              <div>수령 가능한 보상이 없습니다</div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {availableRewards.map((reward, idx) => (
                <div key={idx} style={{
                  padding: 16,
                  background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                  borderRadius: 12,
                  border: "1px solid #f59e0b"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                    fontWeight: 600,
                    color: "#92400e"
                  }}>
                    <span>{REWARD_TYPES.find(rt => rt.id === reward.reward.type)?.icon}</span>
                    <span>{reward.name}</span>
                  </div>
                  <div style={{
                    fontSize: 14,
                    color: "#b45309",
                    marginBottom: 12
                  }}>
                    {REWARD_TYPES.find(rt => rt.id === reward.reward.type)?.name}
                    {reward.reward.amount > 0 && ` +${reward.reward.amount}`}
                  </div>
                  <button
                    onClick={() => onClaimReward(reward)}
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer"
                    }}
                  >
                    수령하기
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 이벤트 생성 모달
function CreateEventModal({ newEvent, setNewEvent, onCreateEvent, onClose }) {
  return (
    <div style={{
      position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(4px)"
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 24,
        padding: 32,
        minWidth: 400,
        maxWidth: 500,
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        maxHeight: "90vh",
        overflowY: "auto"
      }}>
        <h3 style={{
          fontSize: 24,
          fontWeight: 700,
          color: "#374151",
          marginBottom: 24,
          textAlign: "center"
        }}>
          ➕ 길드 이벤트 생성
        </h3>
        
        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
              marginBottom: 8
            }}>
              이벤트명 *
            </label>
            <input
              placeholder="이벤트명을 입력하세요"
              value={newEvent.title}
              onChange={e => setNewEvent({ ...newEvent, title: e.target.value })}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "2px solid #e5e7eb",
                fontSize: 15,
                transition: "border-color 0.2s"
              }}
              onFocus={(e) => e.target.style.borderColor = "#667eea"}
              onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
            />
          </div>
          
          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
              marginBottom: 8
            }}>
              이벤트 설명
            </label>
            <textarea
              placeholder="이벤트에 대한 설명을 입력하세요"
              value={newEvent.description}
              onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
              rows={3}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "2px solid #e5e7eb",
                fontSize: 15,
                resize: "vertical",
                minHeight: 80
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
              marginBottom: 8
            }}>
              이벤트 유형
            </label>
            <select
              value={newEvent.type}
              onChange={e => setNewEvent({ ...newEvent, type: e.target.value })}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "2px solid #e5e7eb",
                fontSize: 15
              }}
            >
              {EVENT_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.icon} {type.name}</option>
              ))}
            </select>
          </div>
          
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16
          }}>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8
              }}>
                시작 시간 *
              </label>
              <input
                type="datetime-local"
                value={newEvent.startDate}
                onChange={e => setNewEvent({ ...newEvent, startDate: e.target.value })}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "2px solid #e5e7eb",
                  fontSize: 15
                }}
              />
            </div>
            
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8
              }}>
                종료 시간 *
              </label>
              <input
                type="datetime-local"
                value={newEvent.endDate}
                onChange={e => setNewEvent({ ...newEvent, endDate: e.target.value })}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "2px solid #e5e7eb",
                  fontSize: 15
                }}
              />
            </div>
          </div>
          
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16
          }}>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8
              }}>
                최대 참가자 수
              </label>
              <input
                type="number"
                min={2}
                max={100}
                value={newEvent.maxParticipants}
                onChange={e => setNewEvent({ ...newEvent, maxParticipants: Number(e.target.value) })}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "2px solid #e5e7eb",
                  fontSize: 15
                }}
              />
            </div>
            
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8
              }}>
                최소 MMR (선택)
              </label>
              <input
                type="number"
                min={0}
                value={newEvent.requirementMMR}
                onChange={e => setNewEvent({ ...newEvent, requirementMMR: Number(e.target.value) })}
                placeholder="0 (제한 없음)"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "2px solid #e5e7eb",
                  fontSize: 15
                }}
              />
            </div>
          </div>
        </div>
        
        <div style={{
          marginTop: 32,
          display: "flex",
          gap: 12,
          justifyContent: "flex-end"
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "12px 24px",
              border: "2px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            취소
          </button>
          <button
            onClick={onCreateEvent}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            생성하기
          </button>
        </div>
      </div>
    </div>
  );
}

// 토너먼트 생성 모달
function CreateTournamentModal({ newTournament, setNewTournament, onCreateTournament, onClose, guildSport }) {
  return (
    <div style={{
      position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(4px)"
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 24,
        padding: 32,
        minWidth: 400,
        maxWidth: 500,
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        maxHeight: "90vh",
        overflowY: "auto"
      }}>
        <h3 style={{
          fontSize: 24,
          fontWeight: 700,
          color: "#374151",
          marginBottom: 24,
          textAlign: "center"
        }}>
          🏆 길드 토너먼트 생성
        </h3>
        
        <div style={{
          background: "#fef3c7",
          padding: 16,
          borderRadius: 12,
          marginBottom: 24,
          border: "1px solid #fbbf24"
        }}>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#92400e",
            marginBottom: 4
          }}>
            💡 토너먼트 안내
          </div>
          <div style={{
            fontSize: 13,
            color: "#b45309"
          }}>
            • 종목: {guildSport} (길드 스포츠 자동 적용)<br/>
            • 생성 권한: 길드장, 부길드장만 가능<br/>
            • 기여도 보상이 자동으로 지급됩니다
          </div>
        </div>
        
        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
              marginBottom: 8
            }}>
              토너먼트명 *
            </label>
            <input
              placeholder="토너먼트명을 입력하세요"
              value={newTournament.title}
              onChange={e => setNewTournament({ ...newTournament, title: e.target.value })}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "2px solid #e5e7eb",
                fontSize: 15
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
              marginBottom: 8
            }}>
              토너먼트 설명
            </label>
            <textarea
              placeholder="토너먼트에 대한 설명을 입력하세요"
              value={newTournament.description}
              onChange={e => setNewTournament({ ...newTournament, description: e.target.value })}
              rows={3}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "2px solid #e5e7eb",
                fontSize: 15,
                resize: "vertical",
                minHeight: 80
              }}
            />
          </div>
          
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16
          }}>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8
              }}>
                경기 일시 *
              </label>
              <input
                type="datetime-local"
                value={newTournament.date}
                onChange={e => setNewTournament({ ...newTournament, date: e.target.value })}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "2px solid #e5e7eb",
                  fontSize: 15
                }}
              />
            </div>
            
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8
              }}>
                신청 마감일 *
              </label>
              <input
                type="datetime-local"
                value={newTournament.deadline}
                onChange={e => setNewTournament({ ...newTournament, deadline: e.target.value })}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "2px solid #e5e7eb",
                  fontSize: 15
                }}
              />
            </div>
          </div>
          
          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
              marginBottom: 8
            }}>
              장소
            </label>
            <input
              placeholder="경기 장소를 입력하세요"
              value={newTournament.location}
              onChange={e => setNewTournament({ ...newTournament, location: e.target.value })}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "2px solid #e5e7eb",
                fontSize: 15
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 600,
              color: "#374151",
              marginBottom: 8
            }}>
              최대 참가자 수
            </label>
            <select
              value={newTournament.maxParticipants}
              onChange={e => setNewTournament({ ...newTournament, maxParticipants: Number(e.target.value) })}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "2px solid #e5e7eb",
                fontSize: 15
              }}
            >
              <option value={8}>8명 (3라운드)</option>
              <option value={16}>16명 (4라운드)</option>
              <option value={32}>32명 (5라운드)</option>
            </select>
          </div>
        </div>
        
        <div style={{
          marginTop: 32,
          display: "flex",
          gap: 12,
          justifyContent: "flex-end"
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "12px 24px",
              border: "2px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            취소
          </button>
          <button
            onClick={onCreateTournament}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            생성하기
          </button>
        </div>
      </div>
    </div>
  );
}

export default GuildEvents;